import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useTradeSettingsContext } from '../../contexts/TradeSettingsContext';
import { supabase } from '../../lib/supabase';
import debounce from 'lodash/debounce';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useI18n } from '../../contexts/I18nContext';
import { TradingRule } from '../../hooks/useTradeSettings';

interface CardSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onReset: () => void;
  onSearch: () => void;
  onCardSelect?: (card: CardSuggestion) => void;
  placeholder?: string;
  selectedCard?: CardSuggestion | null;
}

interface CardSuggestion {
  id: number;
  card_fr: string;
  card_en: string;
  set: string;
  rarity: string;
  image: string;
}

export function CardSearchBar({ 
  value, 
  onChange, 
  onReset, 
  onSearch,
  onCardSelect,
  placeholder = "Rechercher une carte...",
  selectedCard = null
}: CardSearchBarProps) {
  const { t } = useI18n();
  const [localValue, setLocalValue] = useState(value);
  const [suggestions, setSuggestions] = useState<CardSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const { settings } = useTradeSettingsContext();
  const { lang } = useI18n();

  // Synchroniser la valeur locale avec la valeur externe
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const fetchSuggestions = debounce(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      let query = supabase
        .from('cards')
        .select('id, card_fr, card_en, set, rarity, image');

      // Ajouter la condition de recherche selon la langue
      if (lang === 'fr') {
        query = query.ilike('card_fr', `%${searchTerm}%`);
      } else {
        query = query.ilike('card_en', `%${searchTerm}%`);
      }

      // Vérifier que settings existe et contient les données nécessaires
      if (settings && Array.isArray(settings)) {
        const tradingRule = settings[0] as unknown as TradingRule; // Double cast pour éviter l'erreur de type
        
        if (tradingRule.eligible_sets && tradingRule.eligible_sets.length > 0) {
          query = query.in('set', tradingRule.eligible_sets);
        }
        if (tradingRule.eligible_rarities && tradingRule.eligible_rarities.length > 0) {
          query = query.in('rarity', tradingRule.eligible_rarities);
        }
      }

      query = query.limit(5).order(lang === 'fr' ? 'card_fr' : 'card_en');

      const { data, error } = await query;

      if (error) throw error;
      setSuggestions(data || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  useEffect(() => {
    fetchSuggestions(localValue);
  }, [localValue]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!selectedCard) {
        onSearch();
      }
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
    setShowSuggestions(true);
  };

  const handleReset = () => {
    setLocalValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    onReset();
  };

  const handleSuggestionClick = (suggestion: CardSuggestion) => {
    if (onCardSelect) {
      onCardSelect(suggestion);
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative card-search-container">
      {selectedCard ? (
        <div className="flex items-center gap-3 p-2 border border-gray-300 rounded-md">
          <div className="w-12 h-16 flex-shrink-0">
            <img
              src={`${SUPABASE_IMAGE_BASE_URL}${selectedCard.image}`}
              alt={lang === 'fr' ? selectedCard.card_fr : selectedCard.card_en}
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-grow">
            <div className="text-sm font-medium">{lang === 'fr' ? selectedCard.card_fr : selectedCard.card_en}</div>
            <div className="text-xs text-gray-500">
              {selectedCard.set} • {selectedCard.rarity}
            </div>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
          {localValue && (
            <button
              type="button"
              onClick={handleReset}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </>
      )}

      {/* Liste des suggestions */}
      {showSuggestions && !selectedCard && (localValue.length >= 2) && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200">
          {loading ? (
            <div className="p-2 text-sm text-gray-500">{t('common.loading')}</div>
          ) : suggestions.length > 0 ? (
            <ul className="max-h-[400px] overflow-auto">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                >
                  <div className="w-12 h-16 flex-shrink-0">
                    <img
                      src={`${SUPABASE_IMAGE_BASE_URL}${suggestion.image}`}
                      alt={lang === 'fr' ? suggestion.card_fr : suggestion.card_en}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {lang === 'fr' ? suggestion.card_fr : suggestion.card_en}
                    </div>
                    <div className="text-xs text-gray-500">
                      {suggestion.set} • {suggestion.rarity}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-2 text-sm text-gray-500">
              {t('trades.filters.no_suggestions')}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 